import { DfyPaymentMethod } from "@repo/api/dfy";
import { useCallback, useEffect, useMemo } from "react";
import {
  calculateDfyOrderPaymentDetails,
  getDfyOrderTotalCredits,
} from "@repo/api/utils/dfy-billing";
import { useDfyOrderStore } from "@/lib/stores/dfy-order-store/dfy-order-store";

function getAllowedPaymentMethods({
  hasBillingEditPermission,
  isOrganisation,
  creditCoverage,
}: {
  hasBillingEditPermission: boolean;
  isOrganisation: boolean;
  creditCoverage: CreditCoverage;
}) {
  const allowedPaymentMethods: DfyPaymentMethod[] = [];
  const canPayWithStripe = hasBillingEditPermission && !isOrganisation;

  if (
    creditCoverage.type === "FULL" ||
    (creditCoverage.type === "PARTIAL" && canPayWithStripe)
  ) {
    allowedPaymentMethods.push("CREDITS");
  }

  if (canPayWithStripe) {
    allowedPaymentMethods.push("STRIPE");
  }

  return allowedPaymentMethods;
}

export type CreditCoverage = {
  type: "NONE" | "PARTIAL" | "FULL";
  coveredCredits: number;
  remainingCredits: number;
};

function getCreditCoverage({
  creditBalance,
  totalCredits,
}: {
  creditBalance: number;
  totalCredits: number;
}): CreditCoverage {
  if (totalCredits === 0) {
    return { type: "FULL", coveredCredits: 0, remainingCredits: 0 };
  }

  if (creditBalance <= 0) {
    return { type: "NONE", coveredCredits: 0, remainingCredits: totalCredits };
  }

  const type = creditBalance >= totalCredits ? "FULL" : "PARTIAL";
  const coveredCredits =
    creditBalance >= totalCredits ? totalCredits : creditBalance;
  const remainingCredits = totalCredits - coveredCredits;

  return {
    type,
    coveredCredits,
    remainingCredits,
  };
}

export function usePaymentMethod({
  creditBalance,
  isOrganisation,
  hasBillingEditPermission,
}: {
  creditBalance: number;
  isOrganisation: boolean;
  hasBillingEditPermission: boolean;
}) {
  const service = useDfyOrderStore((state) => state.service);
  const details = useDfyOrderStore((state) => state.order!.details);
  const paymentMethod = details.paymentMethod;
  const coupon = useDfyOrderStore((state) => state.coupon);

  const setPaymentMethod = useCallback(
    (newPaymentMethod: DfyPaymentMethod) => {
      service.updateDetails((draft) => {
        draft.paymentMethod = newPaymentMethod;
      });
    },
    [service],
  );

  const paymentDetails = useMemo(() => {
    return calculateDfyOrderPaymentDetails({
      details,
      creditBalance,
      paymentMethod,
      coupon,
    });
  }, [details, creditBalance, paymentMethod, coupon]);

  const totalCredits = getDfyOrderTotalCredits(paymentDetails);

  const creditCoverage = useMemo(() => {
    return getCreditCoverage({
      creditBalance,
      totalCredits,
    });
  }, [creditBalance, totalCredits]);

  const allowedPaymentMethods = useMemo(() => {
    return getAllowedPaymentMethods({
      hasBillingEditPermission,
      isOrganisation,
      creditCoverage,
    });
  }, [hasBillingEditPermission, isOrganisation, creditCoverage]);

  const handleChangePaymentMethod = useCallback(
    (newPaymentMethod: DfyPaymentMethod) => {
      if (!allowedPaymentMethods.includes(newPaymentMethod)) {
        return;
      }

      setPaymentMethod(newPaymentMethod);
    },
    [allowedPaymentMethods, setPaymentMethod],
  );

  useEffect(() => {
    if (
      !allowedPaymentMethods.includes(paymentMethod) &&
      allowedPaymentMethods.length > 0
    ) {
      setPaymentMethod(allowedPaymentMethods[0]!);
    }
  }, [paymentMethod, allowedPaymentMethods, setPaymentMethod]);

  return {
    paymentMethod,
    handleChangePaymentMethod,
    creditCoverage,
    paymentDetails,
    totalCredits,
    allowedPaymentMethods,
  };
}
